import { Injectable } from '@angular/core';
import { County } from '@ptl/global-components';
import { StateStructure } from '@ptl/state';
import { NgRedux } from '@ptl/angular-redux-store';
import { UserInfoService } from 'src/app/angular-elements/shared/services/user-info.service';
import { ReducerUtilityService } from './reducer-utility.service';
import { AgentCubeDemographicInfo } from 'src/app/angular-elements/shared/components/interfaces/agent-cube-demographic-info.interface';
import { saveCrossWalkInfo } from 'src/app/angular-elements/shared/components/configs/reducers/whb-crosswalk.reducer';
import { savePrepopulatedData } from 'src/app/angular-elements/shared/components/configs/reducers/prePopulatedData.reducer';
import { GlobalDataStateStructure } from 'src/app/angular-elements/shared/components/configs/interfaces/globalStateStructure.interface';
import { PrepopulatedFields } from 'src/app/angular-elements/shared/components/interfaces/prepopulated-fields.interface';
import { MemberInfo } from '@ptl/account-management';
import { PrePopulatedDataStateStructure } from 'src/app/angular-elements/shared/components/configs/interfaces/prePopulatedDataStateStructure.interface';
import { DateService } from '../services/date.service';
import { ZipCountyStateStructure } from 'src/app/angular-elements/shared/interfaces/zipCountyStateStructure.interface';
import { STATE_CODE_LABEL_MAPPING } from '../enums/state-code-label.map';
import { SalesForceDemographicAddress, SalesForceDemographicInfo } from '../../angular-elements/shared/components/interfaces/sales-force-demographic-info.interface';
import { WhbCrosswalk } from '../../angular-elements/shared/components/interfaces/member-lookup.interface';

@Injectable({
  providedIn: 'root'
})
export class PrepopulateUserInfoService {
  counties: County[];
  prepopulatedFields: PrepopulatedFields;
  constructor(
    private userInfoService: UserInfoService,
    private ngRedux: NgRedux<StateStructure>,
    private reducerUtilityService: ReducerUtilityService,
    private dateUtility: DateService
  ) { }

  async setPrePopFieldsCustomer(loginInfo) {
    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty') as ZipCountyStateStructure;
    
    this.prepopulatedFields = {
      firstName: loginInfo.accountSettingsInfo.firstName,
      middleInitial: "",
      lastName: loginInfo.accountSettingsInfo.lastName,
      dob: this.dateUtility.getDateAsFormattedString(loginInfo.accountSettingsInfo.dateOfBirth.toString(), 'MM/DD/YYYY'),
      gender: loginInfo.accountSettingsInfo.gender,
      addressLine1: loginInfo.accountSettingsInfo.address1,
      addressLine2: loginInfo.accountSettingsInfo.address2,
      city: loginInfo.accountSettingsInfo.city,
      personalPhoneNumber: loginInfo.accountSettingsInfo.homePhone ? loginInfo.accountSettingsInfo.homePhone.toString() : '',
      emailAddress: loginInfo.accountSettingsInfo.emailAddress,
      mailingAddressExists: 'yes', // should be 'yes' when there are NO mailing fields
      mailingAddressLine1: '',
      mailingAddressLine2: '',
      mailingCity: '',
      mailingState: '',
      mailingZipCode: '',
      memberIdNumber: '',
      zipCode: zipCountyState && zipCountyState.zipCode,
      state: zipCountyState && zipCountyState.county && zipCountyState.county.state,
      memberLookup: null
    };
    this.ngRedux.dispatch(savePrepopulatedData(this.prepopulatedFields));
  }

  async setPrePopFieldsAgentCubed(customerId: string, opportunityId: string): Promise<PrepopulatedFields> {
    const prePopulateData: PrePopulatedDataStateStructure = this.reducerUtilityService.getState('PrePopulateData');
    const agentCubeDemographicInfo: AgentCubeDemographicInfo = await this.userInfoService.getDemographicDataAgentCube(customerId);

    const dobStr = agentCubeDemographicInfo.customerField.individualsField.individualShortField[0].birthDateField;
    //const dateStr = dobStr && dobStr.substring(dobStr.indexOf('(') + 1, dobStr.indexOf(')'));
    //const dob = dateStr && new Date(parseInt(dateStr, 10));

    prePopulateData.prepopulatedFields = {
      firstName: agentCubeDemographicInfo.customerField.individualsField.individualShortField[0].firstNameField,
      middleInitial:
        agentCubeDemographicInfo.customerField.individualsField.individualShortField[0].middleNameField &&
        agentCubeDemographicInfo.customerField.individualsField.individualShortField[0].middleNameField.substr(0, 1),
      lastName: agentCubeDemographicInfo.customerField.individualsField.individualShortField[0].lastNameField,
      dob: dobStr && this.dateUtility.getDateAsFormattedString(dobStr, 'MM/DD/YYYY'),
      gender: '', // Not provided in agent cubed response
      addressLine1: agentCubeDemographicInfo.customerField.address1Field,
      addressLine2: agentCubeDemographicInfo.customerField.address2Field,
      city: agentCubeDemographicInfo.customerField.cityField,
      personalPhoneNumber: agentCubeDemographicInfo.customerField.primaryPhoneField,
      emailAddress: agentCubeDemographicInfo.customerField.individualsField.individualShortField[0].emailField,
      mailingAddressExists: agentCubeDemographicInfo.customerField.mailingAddress1Field ? 'no' : 'yes',  // should be 'no' when there are mailing fields
      mailingAddressLine1: agentCubeDemographicInfo.customerField.mailingAddress1Field,
      mailingAddressLine2: agentCubeDemographicInfo.customerField.mailingAddress2Field,
      mailingCity: agentCubeDemographicInfo.customerField.mailingCityField,
      mailingState: this.reverseLookupStateCode(agentCubeDemographicInfo.customerField.mailingStateAbbrField),
      mailingZipCode: agentCubeDemographicInfo.customerField.mailingZipCodeField,
      memberIdNumber: '',
      state: this.reverseLookupStateCode(agentCubeDemographicInfo.customerField.stateAbbrField),
      zipCode: agentCubeDemographicInfo.customerField.zipCodeField,
      memberLookup: null,
      customerId: customerId,
      opportunityId: opportunityId,
      salesforceAccountId: '',
      salesforceOpportunityId: ''
    };

    this.ngRedux.dispatch(savePrepopulatedData(prePopulateData.prepopulatedFields));

    return prePopulateData.prepopulatedFields;
  }

  async setPrePopFieldsSalesForce(opportunityId: string): Promise<PrepopulatedFields> {
    const prePopulateData: PrePopulatedDataStateStructure = this.reducerUtilityService.getState('PrePopulateData');
    const salesForceDemographicInfo: SalesForceDemographicInfo = await this.userInfoService.getDemographicDataSalesForce(opportunityId);
    const opportunityInfo = salesForceDemographicInfo.opportunity.opportunity;
    const demographicData = salesForceDemographicInfo.prospective_member.account.demographics;
    const primaryAddress = demographicData.address.filter((addr) => addr.type !== 'mailing');
    const mailingAddress = demographicData.address.filter((addr) => addr.type === 'mailing');
    const dob = salesForceDemographicInfo.opportunity.opportunity.birth_date;
    
    //Can distinguish dsnp via this property: opportunity.opportunity.opportunity_segment==='DSNP'

    prePopulateData.prepopulatedFields = {
      firstName: demographicData.first_name,
      middleInitial: demographicData.middle_name && demographicData.middle_name[0],
      lastName: demographicData.last_name,
      dob: dob && this.dateUtility.getDateAsFormattedString(dob, 'MM/DD/YYYY'),
      gender: demographicData.gender ? demographicData.gender[0] : null,
      personalPhoneNumber: opportunityInfo.phone || demographicData.communication?.home_phone || demographicData.communication?.mobile_phone,
      emailAddress: demographicData.communication?.email_address,
      memberIdNumber: '',
      memberLookup: null,
      customerId: '',
      opportunityId: '',
      salesforceAccountId: opportunityInfo.hmfm_salesforce_account_id,
      salesforceOpportunityId: opportunityInfo.hmfm_salesforce_opportunity_id
    } as PrepopulatedFields;

    if (primaryAddress?.[0]) {
      prePopulateData.prepopulatedFields.addressLine1 = primaryAddress[0].addr1;
      prePopulateData.prepopulatedFields.addressLine2 = primaryAddress[0].addr2;
      prePopulateData.prepopulatedFields.state = this.reverseLookupStateCode(primaryAddress[0].state);
      prePopulateData.prepopulatedFields.zipCode = primaryAddress[0].postal_cd;
      prePopulateData.prepopulatedFields.city = primaryAddress[0].city;
    }
    else {
      console.warn('No primary address found for sales force opportunity');
    }

    // This assumes if a mailing address exists it will be different from the primary address
    if (this.isValidSalesForceMailingAddress(primaryAddress, mailingAddress)) {
      prePopulateData.prepopulatedFields.mailingAddressExists = 'no'; // Misleading field name, actually answers the question "Is mailing address the same as primary?"
      prePopulateData.prepopulatedFields.mailingAddressLine1 = mailingAddress[0].addr1;
      prePopulateData.prepopulatedFields.mailingAddressLine2 = mailingAddress[0].addr2;
      prePopulateData.prepopulatedFields.mailingState = this.reverseLookupStateCode(mailingAddress[0].state);
      prePopulateData.prepopulatedFields.mailingZipCode = mailingAddress[0].postal_cd;
      prePopulateData.prepopulatedFields.mailingCity = mailingAddress[0].city;
    }
    else {
      prePopulateData.prepopulatedFields.mailingAddressExists = 'yes'; // Misleading field name, actually answers the question "Is mailing address the same as primary?"
    }

    this.ngRedux.dispatch(savePrepopulatedData(prePopulateData.prepopulatedFields));

    return prePopulateData.prepopulatedFields;
  }

  async setMemberData(memberInfo: MemberInfo) {
    // TODO: this seems to kick off at login, but it's also called when needed on member-lookup. Why do we do both? Clean one up.
    const whbCrosswalk: WhbCrosswalk = await this.userInfoService.doMedigapWHBCrosswalk(memberInfo.umi);
    this.ngRedux.dispatch(saveCrossWalkInfo(whbCrosswalk));

    const prePopulateData: PrePopulatedDataStateStructure = this.reducerUtilityService.getState('PrePopulateData');
    prePopulateData.prepopulatedFields.memberIdNumber = memberInfo.umi;
    this.ngRedux.dispatch(savePrepopulatedData(prePopulateData.prepopulatedFields));
  }

  private reverseLookupStateCode(stateName: string): string {
    let stateCodes = Object.keys(STATE_CODE_LABEL_MAPPING);

    for (let index in stateCodes) {
      let code = stateCodes[index];
      if (STATE_CODE_LABEL_MAPPING[code]?.toLowerCase() === stateName?.toLowerCase()) {
        return code;
      }
    }

    // default back to whatever was given to us in case it was sent as a state code
    return stateName;
  }

  isValidSalesForceMailingAddress(primaryAddress: SalesForceDemographicAddress[], mailingAddress: SalesForceDemographicAddress[]): boolean {
    let primaryAddr = primaryAddress?.[0];
    let mailAddr = mailingAddress?.[0];

    // Check if any fields in mailing address exist
    if (!mailAddr || (!mailAddr.addr1 && !mailAddr.addr2 && !mailAddr.city && !mailAddr.postal_cd && !mailAddr.state)) {
      return false;
    }

    // Check if primary address exists via zip code value (this should really never be false)
    if (!primaryAddr?.postal_cd) {
      return false;
    }

    function trimLCase(val) {
      return val?.toLowerCase().trim();
    }

    // Check if mailing address is the same as the primary
    if (trimLCase(primaryAddr.addr1) === trimLCase(mailAddr.addr1) && 
        trimLCase(primaryAddr.addr2) === trimLCase(mailAddr.addr2) && 
        trimLCase(primaryAddr.city) === trimLCase(mailAddr.city) && 
        trimLCase(primaryAddr.postal_cd) === trimLCase(mailAddr.postal_cd) && 
        trimLCase(primaryAddr.state) === trimLCase(mailAddr.state)) {
      return false;
    }

    return true;
  }
}
